import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Section, StatBadge } from "@/components/Chrome";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function TrustBoundaries() {
  const qc = useQueryClient();
  const { data: trust = [] } = useQuery({ queryKey: ["trust"], queryFn: api.trust.list });
  const update = useMutation({
    mutationFn: ({ action, tier }) => api.trust.update(action, tier),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trust"] }); toast.success("Trust tier updated"); },
  });

  return (
    <div data-testid="trust-page">
      <PageHeader
        eyebrow="Permissions"
        title="Trust boundaries"
        subtitle="Choose which actions Copilot runs autonomously and which require your explicit yes. Anything high-stakes will always ping you on Telegram before executing."
      />
      <Section title="Action tiers">
        <div className="grid grid-cols-12 text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-stone-800 pb-3">
          <div className="col-span-6">Action</div>
          <div className="col-span-4">Description</div>
          <div className="col-span-2 text-right">Tier</div>
        </div>
        <ul className="divide-y divide-stone-800/70" data-testid="trust-list">
          {trust.map((t) => (
            <li key={t.action} className="grid grid-cols-12 py-4 items-center" data-testid={`trust-row-${t.action}`}>
              <div className="col-span-6">
                <div className="text-sm text-stone-100">{t.label}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500 mt-1">{t.action}</div>
              </div>
              <div className="col-span-4 text-xs text-stone-400">{t.description}</div>
              <div className="col-span-2 flex justify-end gap-1">
                {["low", "high"].map((tier) => (
                  <button
                    key={tier}
                    data-testid={`trust-${t.action}-${tier}`}
                    onClick={() => update.mutate({ action: t.action, tier })}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition-colors
                      ${t.tier === tier
                        ? tier === "high"
                          ? "bg-orange-600 border-orange-600 text-white"
                          : "bg-stone-200 border-stone-200 text-stone-900"
                        : "border-stone-700 text-stone-500 hover:border-stone-500 hover:text-stone-200"}`}
                  >
                    {tier === "low" ? "Auto" : "Approve"}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-xs text-stone-500 mt-6 font-mono">
          Auto = Copilot executes silently. Approve = Copilot messages you on Telegram with a Yes / No prompt and waits.
        </p>
      </Section>
    </div>
  );
}
